import { FC, useState } from 'react'
import { IconButton, Table, TableBody, TableRow } from '@mui/material'
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons'

import { StyledButton } from 'Components/Buttons/StyledButton'
import { SortableTableHeader, TableHeaderCell } from 'Components/Table/SortableTableHeader'
import { StyledPaper, StyledTableContainer } from 'Components/Table/StyledTableComponents'
import { StyledTableCell } from 'Components/Table/StyledTableCell'

import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { useGetEmployerMetaFields } from 'Hooks/api/employer/useGetEmployerMetaFields'
import { useDeleteMetaFieldQuery } from 'Hooks/api/JOONAdmin/useDeleteMetaFieldQuery'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useTableOrder } from 'Hooks/useTableOrder'

import { EmployerMetaField } from 'Services/employerService'
import { sortTableRows } from 'Utils/helpers/tables'
import { FlexColumn, FlexRow, spacingSM } from 'Utils/styles/spacing'
import { TableLabel } from 'Utils/styles/text'
import { red } from 'Utils/styles/colors'

import { MetaFieldModal } from './MetaFieldModal/MetaFieldModal'

const headerCells: readonly TableHeaderCell<EmployerMetaField>[] = [
  { id: 'fieldName', numeric: false, label: 'Field name' },
  { id: 'required', numeric: false, label: 'Required' },
  { id: 'unique', numeric: false, label: 'Unique' }
] as const

export const JoonAdminMetaFieldsTable: FC = () => {
  const {
    state: { employerId }
  } = useEmployerInfoContext()

  const { data: metaFields } = useGetEmployerMetaFields(employerId)
  const { order, orderBy, handleRequestSort } = useTableOrder<EmployerMetaField>({
    defaultOrder: 'asc',
    defaultOrderBy: 'fieldName'
  })
  const { showModal: showConfirmationModal } = useConfirmationModalContext()
  const { mutate: deleteMetaField } = useDeleteMetaFieldQuery()

  const [showMetaFieldModal, setShowMetaFieldModal] = useState(false)
  const [selectedMetaField, setSelectedMetaField] = useState<EmployerMetaField | null>(null)

  const openMetaFieldModal = (metaField: EmployerMetaField | null) => {
    setSelectedMetaField(metaField)
    setShowMetaFieldModal(true)
  }

  const handleDelete = async (metaField: EmployerMetaField) => {
    if (employerId === undefined) return

    const confirmed = await showConfirmationModal(
      `Delete field: ${metaField.fieldName}`,
      `This deletes ${metaField.fieldName} and every user's value for it. This can't be undone.`
    )
    if (confirmed) {
      deleteMetaField({ employerId, metaFieldId: metaField.id })
    }
  }

  if (!metaFields) return null

  return (
    <>
      <FlexColumn gap={spacingSM}>
        <FlexRow style={{ justifyContent: 'space-between' }}>
          <TableLabel>Metadata fields</TableLabel>
          <StyledButton
            id="metaFields.add.button.label"
            primary
            onClick={() => openMetaFieldModal(null)}
          />
        </FlexRow>
        <StyledPaper>
          <StyledTableContainer>
            <Table>
              <SortableTableHeader
                headerCells={headerCells}
                order={order}
                orderBy={String(orderBy)}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {sortTableRows(metaFields, order, orderBy).map((metaField) => (
                  <TableRow key={metaField.id}>
                    <StyledTableCell>{metaField.fieldName}</StyledTableCell>
                    <StyledTableCell>{metaField.required ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{metaField.unique ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => openMetaFieldModal(metaField)}
                        aria-label={`Edit ${metaField.fieldName}`}
                        style={{ marginRight: '8px' }}
                      >
                        <Pencil1Icon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(metaField)}
                        aria-label={`Delete ${metaField.fieldName}`}
                        style={{ color: red }}
                      >
                        <TrashIcon />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                ))}
                {metaFields.length === 0 && (
                  <TableRow>
                    <StyledTableCell colSpan={headerCells.length + 1}>
                      No metadata fields yet.
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </StyledPaper>
      </FlexColumn>
      <MetaFieldModal
        showModal={showMetaFieldModal}
        setShowModal={setShowMetaFieldModal}
        metaField={selectedMetaField}
      />
    </>
  )
}
